import { TokenLabel } from "@/components/brand/TokenAmount";
import { Button } from "@/components/ui/Button";
import { useWalletActivity } from "@/hooks/useWalletActivity";
import {
	formatActivityTime,
	type ActivityCategory,
	type ActivityEntry,
} from "@/lib/activity";
import { useAuth } from "@/lib/auth";
import { cn, formatNum } from "@/lib/utils";
import { useEffect, useState } from "react";

const PAGE_SIZE = 12;

const CATEGORIES: Array<{ id: ActivityCategory | "all"; label: string }> = [
	{ id: "all", label: "All" },
	{ id: "vault", label: "Vault" },
	{ id: "transfer", label: "Transfer" },
	{ id: "liquidation", label: "Liq." },
	{ id: "savings", label: "Savings" },
	{ id: "other", label: "Other" },
];

const TOKENS = ["all", "USDM", "MMK", "ICP", "ckBTC", "ckETH", "ckUSDC"] as const;

function shortAccount(s: string | null | undefined): string {
	if (!s) return "—";
	if (s.length <= 20) return s;
	return `${s.slice(0, 10)}…${s.slice(-6)}`;
}

function ActivityRow({ entry }: { entry: ActivityEntry }) {
	const sign =
		entry.direction === "in" ? "+" : entry.direction === "out" ? "−" : "";
	const amountClass =
		entry.direction === "in"
			? "text-primary"
			: entry.direction === "out"
				? "text-destructive"
				: "text-foreground";

	return (
		<li className="rounded-xl border border-border/50 bg-background/40 px-3 py-3 sm:px-4">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0 space-y-1">
					<div className="flex flex-wrap items-center gap-2">
						<span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
							{entry.type}
						</span>
						<span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold capitalize text-muted-foreground">
							{entry.category}
						</span>
						{entry.asset &&
						!["COLLATERAL", "PARAM"].includes(entry.asset) &&
						!entry.asset.includes(":") ? (
							<TokenLabel id={entry.asset} size="sm" />
						) : (
							<span className="text-xs font-medium">{entry.asset}</span>
						)}
					</div>
					{entry.detail ? (
						<p className="line-clamp-2 text-[11px] text-muted-foreground">
							{entry.detail}
						</p>
					) : null}
					<div className="grid gap-0.5 font-mono text-[10px] text-muted-foreground sm:grid-cols-2">
						<p title={entry.fromAccount ?? undefined}>
							<span className="font-sans font-semibold uppercase tracking-wide">
								From
							</span>{" "}
							{shortAccount(entry.fromAccount)}
						</p>
						<p title={entry.toAccount ?? undefined}>
							<span className="font-sans font-semibold uppercase tracking-wide">
								To
							</span>{" "}
							{shortAccount(entry.toAccount)}
						</p>
					</div>
					<p className="text-[10px] text-muted-foreground">
						{formatActivityTime(entry.timestampNs)}
					</p>
				</div>
				<div className="shrink-0 text-right">
					<p className={cn("font-mono-amount text-sm font-bold", amountClass)}>
						{sign}
						{formatNum(entry.amount, entry.amount >= 100 ? 2 : 4)}
					</p>
					<p className="mt-0.5 text-[10px] capitalize text-muted-foreground">
						{entry.status} · {entry.source.replace("_", " ")}
					</p>
				</div>
			</div>
		</li>
	);
}

export function WalletActivityPanel({
	initialCategory = "all",
}: {
	/** Pre-select filter (e.g. liquidation history tab). */
	initialCategory?: ActivityCategory | "all";
}) {
	const { isAuthenticated, login, isLoggingIn } = useAuth();
	const [category, setCategory] = useState<ActivityCategory | "all">(
		initialCategory,
	);
	const [token, setToken] = useState<(typeof TOKENS)[number]>("all");
	const [fromDay, setFromDay] = useState("");
	const [toDay, setToDay] = useState("");
	const [page, setPage] = useState(0);

	const {
		entries,
		isLoading,
		isError,
		error,
		total,
		hasNext,
		sources,
		protocolError,
		usdmIdx,
		mmkIdx,
		refetch,
	} = useWalletActivity({
		category,
		asset: token,
		fromDay,
		toDay,
		page,
		pageSize: PAGE_SIZE,
	});

	useEffect(() => {
		setPage(0);
	}, [category, token, fromDay, toDay]);

	if (!isAuthenticated) {
		return (
			<div className="glass-panel p-5 lg:col-span-2">
				<p className="section-eyebrow">Activity</p>
				<p className="mt-3 text-sm text-muted-foreground">
					Connect with Internet Identity to load protocol events and ledger
					transfers for your principal.
				</p>
				<Button
					className="mt-4"
					loading={isLoggingIn}
					onClick={() => void login()}
				>
					Connect
				</Button>
			</div>
		);
	}

	return (
		<div className="glass-panel space-y-4 p-5 lg:col-span-2">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<p className="section-eyebrow">Activity</p>
					<p className="mt-1 text-sm text-muted-foreground">
						Vault deposits, withdraws, mints, repays, liquidations, savings, and
						wallet transfers (USDM/MMK index when configured; other assets from
						this device).
					</p>
					{protocolError ? (
						<p className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
							{protocolError}
						</p>
					) : null}
					<p className="mt-1 text-[11px] text-muted-foreground">
						Sources: {sources.length ? sources.join(" · ") : "protocol_flow"}
						{!usdmIdx && !mmkIdx
							? " · ledger index optional (set VITE_CANISTER_ID_USDM_INDEX / MMK_INDEX)"
							: ""}
						{" · "}
						{total} matching
					</p>
				</div>
				<button
					type="button"
					className="text-[11px] font-semibold text-primary"
					onClick={() => void refetch()}
				>
					Refresh
				</button>
			</div>

			<div className="flex flex-wrap gap-1.5">
				{CATEGORIES.map((c) => (
					<button
						key={c.id}
						type="button"
						onClick={() => setCategory(c.id)}
						className={cn(
							"min-h-8 rounded-full border px-3 text-xs font-semibold",
							category === c.id
								? "border-primary bg-primary/15 text-primary"
								: "border-border text-muted-foreground",
						)}
					>
						{c.label}
					</button>
				))}
			</div>

			<div className="flex flex-wrap items-end gap-2">
				<label className="text-[11px] text-muted-foreground">
					Token
					<select
						value={token}
						onChange={(e) =>
							setToken(e.target.value as (typeof TOKENS)[number])
						}
						className="mt-1 block h-9 rounded-lg border border-border bg-background px-2 text-xs"
					>
						{TOKENS.map((t) => (
							<option key={t} value={t}>
								{t === "all" ? "All tokens" : t}
							</option>
						))}
					</select>
				</label>
				<label className="text-[11px] text-muted-foreground">
					From
					<input
						type="date"
						value={fromDay}
						onChange={(e) => setFromDay(e.target.value)}
						className="mt-1 block h-9 rounded-lg border border-border bg-background px-2 text-xs"
					/>
				</label>
				<label className="text-[11px] text-muted-foreground">
					To
					<input
						type="date"
						value={toDay}
						onChange={(e) => setToDay(e.target.value)}
						className="mt-1 block h-9 rounded-lg border border-border bg-background px-2 text-xs"
					/>
				</label>
			</div>

			{isLoading ? (
				<ul className="space-y-2">
					{Array.from({ length: 4 }).map((_, i) => (
						<li key={i} className="skeleton-shimmer h-16 rounded-xl" />
					))}
				</ul>
			) : isError ? (
				<p className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
					{error instanceof Error ? error.message : "Failed to load activity"}
				</p>
			) : entries.length === 0 ? (
				<div className="space-y-2 py-6 text-center text-sm text-muted-foreground">
					<p>
						No activity for this principal yet (or filters hide all rows).
					</p>
					<p className="text-[11px]">
						Sources: protocol treasury flows for your principal
						{usdmIdx || mmkIdx
							? " plus USDM/MMK ledger index transfers"
							: " only — set VITE_CANISTER_ID_USDM_INDEX / MMK_INDEX for transfer history"}
						. Mint, deposit, repay, save, liquidate, or send tokens to create
						events.
					</p>
				</div>
			) : (
				<ul className="space-y-2">
					{entries.map((e) => (
						<ActivityRow key={e.id} entry={e} />
					))}
				</ul>
			)}

			{total > PAGE_SIZE ? (
				<div className="flex items-center justify-between gap-2 pt-1">
					<button
						type="button"
						disabled={page <= 0}
						onClick={() => setPage((p) => Math.max(0, p - 1))}
						className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
					>
						Previous
					</button>
					<span className="text-[11px] text-muted-foreground">
						Page {page + 1} · {total} total
					</span>
					<button
						type="button"
						disabled={!hasNext}
						onClick={() => setPage((p) => p + 1)}
						className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
					>
						Next
					</button>
				</div>
			) : null}
		</div>
	);
}
